// Libraries
import dayjs from "dayjs";
import "dayjs/locale/de";
import isoWeek from "dayjs/plugin/isoWeek";
import { encodePlaceNameSegment } from "~/operations/shared";
dayjs.extend(isoWeek);
dayjs.locale("de");

type TableType = "heizoelpreise" | "pelletspreise";

type CityOil = {
  name: string;
  zipcode: string;
  currentPrice: {
    modifiedAt: number;
    value: number;
  };
  lastPrice: {
    modifiedAt: number;
    value: number;
  };
  relativeChange: number;
};

type TableHeaderData = string[];
type TableRowData = (string | number | { text: string; href: string })[][];

export const returnFormattedDate = (dateString: string, dateFormat: string) => {
  dayjs.locale("de");
  let retVal = dayjs(dateString).format(dateFormat);
  if (dateFormat === "today") retVal = dayjs().format("DD.MM.YYYY");
  if (dateFormat === "day") retVal = dayjs(dateString).format("DD");
  if (dateFormat === "weekDay") retVal = dayjs(dateString).format("dddd");
  if (dateFormat === "month") retVal = dayjs(dateString).format("MMMM");
  if (dateFormat === "year") retVal = dayjs(dateString).format("YYYY");

  return retVal;
};

export const returnPriceFormatDE = (price: number | string) => {
  if (price === null) return;
  const NumberPrice = Number(price);
  return NumberPrice.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
  });
};

export const returPriceTrendFormatDE = (price: number | string) => {
  if (price === null) return;
  const NumberPrice = Number(price);
  if (Number(NumberPrice.toFixed(2)) < 0)
    return NumberPrice.toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR",
    });
  if (Number(NumberPrice.toFixed(2)) === 0) return `0,00 €`;
  return `+${NumberPrice.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
  })}`;
};

export const getFormattedPercentage = (
  currentPrice: number,
  lastPrice: number,
) => {
  if (currentPrice == null || lastPrice == null) return "-";
  const relativeChangePercentage =
    ((currentPrice - lastPrice) / lastPrice) * 100;
  return __returnPriceTrendPercentDE(relativeChangePercentage);
};

const __returnPriceTrendPercentDE = (percentage: number | string) => {
  if (percentage === null) return;
  const NumberPrice = Number(percentage);
  if (NumberPrice <= 0)
    return `${NumberPrice.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  return `+${NumberPrice.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

export const formatNumberAsPercentage = (value: number | null): string => {
  return (
    Math.abs(value ?? 0).toLocaleString("de-DE", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }) + "%"
  );
};

export const getCurrentDate = () => {
  const currentDate = new Date();
  const day = currentDate.getDate();
  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear().toString().slice(-2);

  const formattedDate = `${day < 10 ? "0" + day : day}.${
    month < 10 ? "0" + month : month
  }.${year}`;
  return formattedDate;
};

export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const replaceToUmlauts = (param: string): string => {
  return param
    .replaceAll("ae", "ä")
    .replaceAll("oe", "ö")
    .replaceAll("ue", "ü");
};

export const getTableHeaderData = (
  topCitiesData: CityOil[],
): TableHeaderData | undefined => {
  if (!topCitiesData?.length) return;

  const oilData = topCitiesData as CityOil[];
  return [
    "Stadt",
    `Preis am ${dayjs
      .unix(oilData[0].currentPrice.modifiedAt)
      .format("DD.MM")}`,
    `Preis am ${dayjs.unix(oilData[0].lastPrice.modifiedAt).format("DD.MM")}`,
    "Differenz",
  ];
};

export const formatTopCitiesTableData = (
  topCitiesData: CityOil[],
  type: TableType,
): TableRowData => {
  const formattedData: TableRowData = [];

  const filteredCities = topCitiesData.filter(
    (city) =>
      (city.currentPrice?.value ?? 0) > 0 || (city.lastPrice?.value ?? 0) > 0,
  );

  filteredCities.forEach((city) => {
    const oilCity = city as CityOil;

    const currentPrice = oilCity.currentPrice?.value;
    const lastPrice = oilCity.lastPrice?.value;
    const relativeChange = oilCity.relativeChange;

    formattedData.push([
      {
        text: oilCity.name,
        href: `/${type}/${type}-${encodePlaceNameSegment(oilCity.name)}-${oilCity.zipcode}`,
      },
      returnPriceFormatDE(currentPrice),
      returnPriceFormatDE(lastPrice),
      returPriceTrendFormatDE(relativeChange),
    ]);
  });
  return formattedData;
};
